import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import CareerGrowthLogo from '../components/CareerGrowthLogo';
import QuizQuestion from '../components/QuizQuestion';
import AuthPanel from '../components/AuthPanel';
import { QUIZ_REGISTRY } from '../config/quizzes';
import { getApplicableQuestions, isQuestionAnswered } from '../utils/quizAnswers';
import { api } from '../api/client';

// Math.random()-based UUID v4. This id is only ever used as an opaque
// session identifier (not a security token), so it doesn't need
// cryptographic randomness — it just needs to be a syntactically valid UUID,
// because the backend types the session_id path param as `uuid.UUID` and
// 422s on anything else (see FJCS-backend app/api/routes/b2b/public_quiz.py
// and quiz.py). A non-UUID-shaped fallback id silently 422s every PUT/POST
// for that session — total silent data loss for the affected browser.
function uuidV4Fallback() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function makeSessionId() {
  return typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : uuidV4Fallback();
}

function getOrCreateSessionId(slug) {
  const key = `quiz-session-${slug}`;
  try {
    const existing = localStorage.getItem(key);
    if (existing) return existing;
  } catch { /* localStorage unavailable (private mode) */ }
  const id = makeSessionId();
  try { localStorage.setItem(key, id); } catch { /* ignore */ }
  return id;
}

function loadStoredAnswers(slug) {
  try {
    const raw = localStorage.getItem(`quiz-answers-${slug}`);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function saveStoredAnswers(slug, answers) {
  try {
    localStorage.setItem(`quiz-answers-${slug}`, JSON.stringify(answers));
  } catch { /* ignore */ }
}

export default function QuizScreen() {
  const { slug } = useParams();
  const quiz = QUIZ_REGISTRY[slug];

  const [sessionId] = useState(() => (quiz ? getOrCreateSessionId(slug) : null));
  const [answers, setAnswers] = useState(() => (quiz ? loadStoredAnswers(slug) : {}));

  // Computed once, from the localStorage-hydrated answers — NOT re-derived on
  // every answers change, so editing an answer from the auth screen (via
  // Back) never gets silently overridden back into the auth phase.
  const [initialPhaseState] = useState(() => {
    if (!quiz) return { phase: 'questions', index: 0 };
    const applicable = getApplicableQuestions(quiz.questions, answers);
    const allAnswered = applicable.length > 0 && applicable.every((q) => isQuestionAnswered(q, answers[q.key]));
    return allAnswered
      ? { phase: 'auth', index: Math.max(applicable.length - 1, 0) }
      : { phase: 'questions', index: 0 };
  });

  const [phase, setPhase] = useState(initialPhaseState.phase);
  const [currentIndex, setCurrentIndex] = useState(initialPhaseState.index);

  const applicableQuestions = useMemo(
    () => (quiz ? getApplicableQuestions(quiz.questions, answers) : []),
    [quiz, answers],
  );

  // Narrow safety net, not a reactive phase sync: if branching ever shrinks
  // `applicableQuestions` out from under a `currentIndex` that pointed past
  // the new end (e.g. a future quiz config where a later answer skips an
  // *earlier* question — not reachable with today's url-quiz config, whose
  // one skipIf only ever looks at an earlier answer), clamp the index back
  // in bounds. Deliberately does NOT touch `phase` — see the "Back from the
  // auth phase" regression test in QuizScreen.test.jsx for why a useEffect
  // that reactively derives `phase` from `answers`/`applicableQuestions` is
  // exactly the bug class this file avoids. This effect only ever moves
  // `currentIndex` downward to stay within range; it never widens or resets
  // it, and it's a no-op whenever the list hasn't shrunk past the cursor.
  useEffect(() => {
    if (phase !== 'questions') return;
    const maxIndex = Math.max(applicableQuestions.length - 1, 0);
    if (currentIndex > maxIndex) setCurrentIndex(maxIndex);
  }, [applicableQuestions.length, phase]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!quiz) {
    return (
      <div className="min-h-screen bg-[#F6F7FB] flex items-center justify-center px-6">
        <p className="text-gray-500">Quiz not found.</p>
      </div>
    );
  }

  const total = applicableQuestions.length;
  const currentQuestion = applicableQuestions[currentIndex];
  const currentValue = currentQuestion ? answers[currentQuestion.key] : undefined;
  const canProceed = currentQuestion ? isQuestionAnswered(currentQuestion, currentValue) : true;
  const isLastQuestion = currentIndex >= total - 1;

  const handleAnswerChange = (value) => {
    if (!currentQuestion) return;
    const next = { ...answers, [currentQuestion.key]: value };
    setAnswers(next);
    saveStoredAnswers(slug, next);
  };

  const handleNext = () => {
    if (!currentQuestion || !canProceed) return;
    api
      .put(`/api/b2b/public/quiz/${slug}/${sessionId}`, {
        question_key: currentQuestion.key,
        // Coalesce to null (not undefined) so the key survives JSON.stringify —
        // an untouched optional question (e.g. the final text question left
        // blank) has no entry in `answers` at all, and JSON.stringify silently
        // drops undefined-valued keys, which previously produced a body with
        // no `value` key and a 422 from the backend's required `Any` field.
        value: answers[currentQuestion.key] ?? null,
      })
      .catch(() => {});

    if (isLastQuestion) {
      api.post(`/api/b2b/public/quiz/${slug}/${sessionId}/complete`, {}).catch(() => {});
      setPhase('auth');
    } else {
      setCurrentIndex((i) => i + 1);
    }
  };

  const handleBack = () => {
    if (phase === 'auth') {
      setPhase('questions');
      setCurrentIndex(Math.max(total - 1, 0));
      return;
    }
    setCurrentIndex((i) => Math.max(i - 1, 0));
  };

  const handleAuthSuccess = () => {
    api.post(`/api/b2b/quiz/${slug}/${sessionId}/link`, {}).catch(() => {});
  };

  if (phase === 'auth') {
    return (
      <div className="min-h-screen bg-[#F6F7FB] flex items-center justify-center px-4 py-10">
        <div className="w-full max-w-md">
          <button
            type="button"
            onClick={handleBack}
            aria-label="Back"
            className="mb-4 text-gray-400 hover:text-gray-600"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="text-center mb-4">
            <h2 className="text-xl font-bold text-[#1E293B]">Create your free workspace to see your report</h2>
            <p className="text-sm text-gray-500 mt-1">
              Already have one? Sign in — your answers are saved either way.
            </p>
          </div>
          <AuthPanel
            mode="route"
            lockAccountType="employer"
            defaultTab="register"
            onAuthSuccess={handleAuthSuccess}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F6F7FB] flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-xl">
        <div className="flex flex-col items-center mb-6">
          <CareerGrowthLogo />
        </div>
        <div className="bg-white rounded-[32px] shadow-2xl px-8 py-10">
          <div className="flex items-center justify-between mb-6">
            {currentIndex > 0 ? (
              <button
                type="button"
                onClick={handleBack}
                aria-label="Back"
                className="text-gray-400 hover:text-gray-600"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
            ) : (
              <span />
            )}
            <span className="text-sm font-semibold text-gray-500">
              {currentIndex + 1} / {total}
            </span>
          </div>

          <div className="h-1.5 bg-gray-100 rounded-full mb-8 overflow-hidden">
            <div
              className="h-full bg-[#F47920] rounded-full transition-all"
              style={{ width: `${total > 0 ? ((currentIndex + 1) / total) * 100 : 0}%` }}
            />
          </div>

          {currentIndex === 0 && quiz.intro && (
            <div className="mb-6 rounded-2xl bg-[#F6F7FB] px-4 py-3 text-xs text-gray-500 leading-relaxed">
              {quiz.intro}
            </div>
          )}

          {currentQuestion && (
            <QuizQuestion question={currentQuestion} value={currentValue} onChange={handleAnswerChange} />
          )}

          <button
            type="button"
            onClick={handleNext}
            disabled={!canProceed}
            className="w-full mt-8 bg-[#F47920] hover:bg-[#E06810] text-white text-[16px] font-bold py-4 rounded-2xl transition-all shadow-md shadow-orange-200 active:scale-[0.99] disabled:opacity-50"
          >
            {isLastQuestion ? 'Finish' : 'Next'}
          </button>
        </div>
      </div>
    </div>
  );
}
